import {useCallback, useId, useLayoutEffect, useMemo, useRef, useState} from 'react'
import type React from 'react'

export interface UseDisclosureOptions {
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
  returnFocusOnClose?: boolean
}

export interface DisclosureTriggerAriaProps {
  id: string
  'aria-expanded': boolean
  'aria-controls': string
  onClick: () => void
  ref: React.RefCallback<HTMLElement>
}

export interface DisclosurePanelAriaProps {
  id: string
  'aria-labelledby'?: string
}

export const useDisclosure = (options?: UseDisclosureOptions) => {
  const {open: controlledOpen, defaultOpen = false, onOpenChange, returnFocusOnClose = true} = options ?? {}

  const id = useId()
  const triggerId = `disclosure-trigger-${id}`
  const panelId = `disclosure-panel-${id}`

  const [internalOpen, setInternalOpen] = useState(defaultOpen)
  const isControlled = controlledOpen !== undefined
  const open = isControlled ? controlledOpen : internalOpen

  const triggerRef = useRef<HTMLElement | null>(null)

  const setOpen = useCallback(
    (next: boolean) => {
      if (next === open) return
      if (!isControlled) setInternalOpen(next)
      onOpenChange?.(next)
    },
    [open, isControlled, onOpenChange],
  )

  const toggle = useCallback(() => {
    setOpen(!open)
  }, [open, setOpen])

  const previousOpenRef = useRef(open)
  useLayoutEffect(() => {
    if (previousOpenRef.current && !open && returnFocusOnClose) {
      requestAnimationFrame(() => triggerRef.current?.focus())
    }
    previousOpenRef.current = open
  }, [open, returnFocusOnClose])

  const triggerCallbackRef = useCallback((node: HTMLElement | null) => {
    triggerRef.current = node
  }, [])

  const getTriggerProps = useCallback(
    (): DisclosureTriggerAriaProps => ({
      id: triggerId,
      'aria-expanded': open,
      'aria-controls': panelId,
      onClick: toggle,
      ref: triggerCallbackRef,
    }),
    [triggerId, open, panelId, toggle, triggerCallbackRef],
  )

  const getPanelProps = useCallback(
    (opts?: {labelledBy?: boolean}): DisclosurePanelAriaProps =>
      opts?.labelledBy ? {id: panelId, 'aria-labelledby': triggerId} : {id: panelId},
    [panelId, triggerId],
  )

  return useMemo(
    () => ({open, setOpen, toggle, triggerId, panelId, triggerRef, getTriggerProps, getPanelProps}),
    [open, setOpen, toggle, triggerId, panelId, getTriggerProps, getPanelProps],
  )
}
